import {useCallback} from 'react';

import {NO_APP_ID, ScreenshotTab} from '@web-catalog/const';
import {FAILED_AUTH_INDEX} from '@web-catalog/const/numbers';

import {App, AppType} from '@web-catalog/types';

import {useAuthorizedProfile} from '@web-catalog/store/auth';

import {
  EcommerceEventParams,
  emitEcommerceEvent,
  emitYmEvent,
  logTmrEvent,
} from '@web-catalog/utils/analytics';

type LogAppPageViewProps = {
  app_id: string;
  app_category_name: string;
  app_subcategory_name: string;
  app_name: string;
  dev_name: string;
  app_curr_version: string;
  app_price: string;
  isStub: number;
};

type LogGamePageViewProps = LogAppPageViewProps;

const buildEcommerceParams = (
  params: LogAppPageViewProps,
  appType: AppType,
  appId: number,
  categoryNames: Record<string, string>,
): EcommerceEventParams['app'] => {
  return {
    type: appType,
    category:
      categoryNames[params.app_category_name] ?? params.app_category_name,
    developer: params.dev_name,
    price: Number.parseFloat(params.app_price),
    packageName: params.app_id,
    appId,
  };
};

export const useAppViewAnalytics = () => {
  const userInfo = useAuthorizedProfile();
  const logPageView = useCallback(
    (
      params: LogAppPageViewProps | LogGamePageViewProps,
      appType: AppType,
      appId: number,
      categoryNames: Record<string, string>,
      tags: Array<string>,
      rars?: string,
    ) => {
      logTmrEvent({
        logEvent: appType === AppType.GAMES ? 'gamePage.view' : 'appPage.view',
        params: {
          ...params,
          tags: tags.join(',').substring(0, 255),
          vk_id: userInfo?.id ?? FAILED_AUTH_INDEX,
          rars,
        },
      });

      if (appId === NO_APP_ID) return;

      emitYmEvent({eventName: 'ecommerce_view_item'});
      emitEcommerceEvent({
        eventName: 'view_item',
        app: buildEcommerceParams(params, appType, appId, categoryNames),
      });
    },
    [userInfo?.id],
  );

  const logAppInstallViaRustoreClick = useCallback(
    (
      params: LogAppPageViewProps,
      appType: AppType,
      appId: number,
      categoryNames: Record<string, string>,
      reportClickDownload: boolean,
    ) => {
      logTmrEvent({logEvent: 'appInstallViaRustore.click', params});

      emitYmEvent({
        eventName: 'ecommerce_add_to_cart',
        params: {app_id_311466125: params.app_id},
      });

      emitEcommerceEvent({
        eventName: 'add_to_cart',
        app: buildEcommerceParams(params, appType, appId, categoryNames),
      });

      if (reportClickDownload) {
        logTmrEvent({logEvent: 'appPage.click.download', params});
        emitYmEvent({eventName: 'appPage_click_dowload'});
      }
    },
    [],
  );

  const logAppInstallViaRustoreTvClick = useCallback(
    (params: LogAppPageViewProps) => {
      logTmrEvent({
        logEvent: 'appInstallViaRustoreTV.click',
        params: {
          ...params,
          vk_id: userInfo?.id ?? FAILED_AUTH_INDEX,
        },
      });
    },
    [userInfo?.id],
  );

  const logAppFullDescriptionCollapse = useCallback(
    (appId: string) => {
      logTmrEvent({
        logEvent: 'appFullDescription.collapse',
        params: {
          app_id: appId,
          vk_id: userInfo?.id ?? FAILED_AUTH_INDEX,
        },
      });
    },
    [userInfo?.id],
  );

  const logAppFullDescriptionOpen = useCallback(
    (appId: string) => {
      logTmrEvent({
        logEvent: 'appFullDescription.open',
        params: {
          app_id: appId,
          vk_id: userInfo?.id ?? FAILED_AUTH_INDEX,
        },
      });

      emitYmEvent({
        eventName: 'detail_button_more_details',
        params: {app_id_311741888: appId},
      });
    },
    [userInfo?.id],
  );

  const logVersionsHistoryClick = useCallback(
    (appId: string) => {
      logTmrEvent({
        logEvent: 'versionsHistory.click',
        params: {
          app_id: appId,
          vk_id: userInfo?.id ?? FAILED_AUTH_INDEX,
        },
      });
    },
    [userInfo?.id],
  );

  const logDevPageClick = useCallback(
    (appId: string, devName: string) => {
      logTmrEvent({
        logEvent: 'devPage.click',
        params: {
          app_id: appId,
          dev_name: devName,
          vk_id: userInfo?.id ?? FAILED_AUTH_INDEX,
        },
      });

      emitYmEvent({
        eventName: 'detail_button_developer_name',
        params: {app_id_311741832: appId},
      });
    },
    [userInfo?.id],
  );

  const logTabClick = useCallback(
    (
      appId: string,
      category: string,
      tab: keyof typeof ScreenshotTab,
      appType?: AppType,
    ) => {
      const event =
        tab === ScreenshotTab.MOBILE ? 'phoneMode.click' : 'tabletMode.click';

      const logEventPrefix = appType === AppType.GAMES ? 'gamePage' : 'appPage';

      logTmrEvent({
        logEvent: `${logEventPrefix}.${event}`,
        params: {
          app_category_name: category,
          app_id: appId,
          vk_id: userInfo?.id ?? FAILED_AUTH_INDEX,
        },
      });
    },
    [userInfo?.id],
  );

  const logAggregatorHelpOpen = useCallback(
    (
      package_name: string,
      manufacturer: string | undefined | null,
      aggregator_name: string | undefined | null,
    ) => {
      logTmrEvent({
        logEvent: 'aggregatorHelp.open',
        params: {
          package_name,
          manufacturer,
          aggregator_name,
          vk_id: userInfo?.id ?? FAILED_AUTH_INDEX,
        },
      });
    },
    [userInfo?.id],
  );

  const logMoreAppsClick = useCallback((appId: string) => {
    emitYmEvent({
      eventName: 'detail_button_more_applications',
      params: {app_id_311741890: appId},
    });
  }, []);

  const logAllReviewsClick = useCallback((app: App) => {
    emitYmEvent({
      eventName: 'detail_button_all_reviews',
      params: {app_id_311741916: app.packageName},
    });
    logTmrEvent({
      logEvent: 'appPage.allReviews.click',
      params: {app_id: app.packageName, app_name: app.appName},
    });
  }, []);

  const logAllReviewsView = useCallback((app: App) => {
    logTmrEvent({
      logEvent: 'appPage.allReviews.view',
      params: {app_id: app.packageName, app_name: app.appName},
    });
  }, []);

  const logRestrictionsClick = useCallback(
    (appId: string) => {
      logTmrEvent({
        logEvent: 'restrictions.click',
        params: {
          app_id: appId,
          vk_id: userInfo?.id ?? FAILED_AUTH_INDEX,
        },
      });
    },
    [userInfo?.id],
  );

  const logRestrictionsOpen = useCallback(
    (
      rars: string,
      app_category_name: string,
      app_name: string,
      app_id: string,
    ) => {
      logTmrEvent({
        logEvent: 'appPage.rars.view',
        params: {
          rars,
          app_category_name,
          app_name,
          app_id,
        },
      });
    },
    [],
  );

  return {
    logPageView,
    logAppInstallViaRustoreClick,
    logAppFullDescriptionCollapse,
    logAppFullDescriptionOpen,
    logVersionsHistoryClick,
    logDevPageClick,
    logTabClick,
    logAggregatorHelpOpen,
    logMoreAppsClick,
    logAllReviewsClick,
    logAllReviewsView,
    logRestrictionsClick,
    logRestrictionsOpen,
    logAppInstallViaRustoreTvClick,
  };
};
